<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters class="page-header py-4">
      <d-row class="w-100">
        <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">
            Zamówienia
          </span>
          <h3 class="page-title">Lista zamówień</h3>
        </d-col>
        <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end offset-special">
          <d-button theme="success"
            @click.prevent="$router.push('/zamowienia/nowe')">
            <i class="material-icons">add</i>
            Dodaj zamówienie
          </d-button>
        </d-col>
      </d-row>

      <d-row
        v-if="allDataLoaded"
        style="width: 100%;"
        class="d-flex ml-0 mt-3 page-filters">
        <validation-observer
          ref="orders-filters-form"
          tag="form"
          style="width: 100%;"
          class="d-flex">
          <d-input
            placeholder="Podaj nazwę zamówienia lub klienta"
            v-model="filters.search"
            :class="{ 'w-25': !inPopup, 'w-50': inPopup }" />

          <select-customer
            v-if="!inPopup"
            class="w-25 ml-2"
            :customerID="filters.customer_id" />

          <v-select
            v-if="!inPopup"
            class="ml-1 w-25"
            :options="orderWorkerOptions"
            v-model="filters.worker_id"
            placeholder="Wybierz pracownika"
            :searchable="true"
            label="text">
          </v-select>

          <v-select
            v-if="!inPopup"
            class="ml-1 w-25"
            :options="orderStatusOptions"
            v-model="filters.status"
            placeholder="Status zamówienia"
            label="text">
          </v-select>

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
              'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
            }"
            class="ml-1 w-25"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
            </template>
          </date-range-picker>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-2"
            @click.prevent="validateFiltersForm">Szukaj</d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">Resetuj filtry</d-button>
        </validation-observer>
      </d-row>

      <d-row
        v-if="hasSelectedItems"
        class="d-flex ml-0 mt-3 page-filters">
        <div class="d-flex pt-1" style="z-index: 1;">
          Wybrane elementy:
        </div>

        <d-button
          theme="success"
          :disabled="invoiceInProgress"
          @click.native="markManyAsInvoiced()"
          :outline="true"
          title="Oznacz jako zafakturowane"
          class="ml-3">
          Oznacz jako zafakturowane
        </d-button>

        <d-button
          theme="danger"
          :disabled="invoiceInProgress"
          @click.native="markManyAsNotInvoiced()"
          :outline="true"
          title="Cofnij zafakturowanie"
          class="ml-3">
          Cofnij zafakturowanie
        </d-button>
      </d-row>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 pr-0" style="width: 50px;">
              <d-checkbox
                inline
                v-model="toggleAllItems"
                @change="updateAllItems()" />
            </th>
            <th scope="col" class="border-0">Status</th>
            <th scope="col" class="border-0">Zamówienie</th>
            <th scope="col" class="border-0">Klient</th>
            <th scope="col" class="border-0">Planowana wysyłka</th>
            <th scope="col" class="border-0">Produkty</th>
            <th scope="col" class="border-0" style="width: 150px;">Stan</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index"
            :class="{ 'has-unread-logs': order.has_unread_logs }">
            <td class="align-middle pr-0">
              <d-checkbox
                inline
                v-model="selectedItems[order.id]"/>
            </td>
            <td class="align-middle">
              <d-badge
                v-if="order.status && orderStatusName[order.status] && orderStatusClass[order.status]"
                :theme="orderStatusClass[order.status]"
                :outline="true">
                {{ orderStatusName[order.status] }}
              </d-badge>

              <d-badge
                v-if="!order.status"
                theme="secondary"
                :outline="true">
                Brak
              </d-badge>
            </td>
            <td class="align-middle">
              <d-badge
                v-if="order.has_unread_logs"
                theme="danger"
                class="mr-2"
                title="Zamówienie posiada nieprzeczytane powiadomienia">
                !
              </d-badge>
              {{ order.order_name }}
              <small
                v-if="order.invoice_number"
                class="d-block">
                <strong>Nr faktury:</strong> <span class="text-primary">{{ order.invoice_number }}</span>
              </small>
            </td>
            <td class="align-middle">
              {{ order.customer_name }}
              <template v-if="order.sub_customer_name">
                <br>
                <small>
                  Wykonawca 1: <strong>{{ order.sub_customer_name }}</strong>
                </small>
              </template>
            </td>
            <td class="align-middle">
              <span :class="plannedShipmentDateColor(order.planned_shipment_date)">{{ order.planned_shipment_date }}</span>
            </td>
            <td class="align-middle">
              <d-badge
                @click.native="showOrderProducts(order.order_name, order.products)"
                class="cursor-pointer"
                :theme="!order.products_has_usages ? 'danger' : 'primary'">
                {{ order.order_products_count }}
              </d-badge>

              <i
                v-if="!order.products_has_usages"
                class="material-icons text-danger pl-2"
                style="position: relative; top: 4px;"
                title="Niektóre produkty nie mają zdefiniowanego zużycia w tym zamówieniu">
                warning
              </i>
            </td>
            <td class="align-middle">
              <small>
                Wykonano: <span :class="orderStatus(order)">
                  {{ order.products_done_count }} / {{ order.products_with_procedure_count }}
                </span>
              </small>
              <br>
              <small>
                Skrojone:
                <span v-if="order.is_pattern_finished" class="text-success">Tak</span>
                <span v-else class="text-danger">Nie</span>
              </small>
              <br>
              <small>
                Szycie:
                <span v-if="order.sewing_done_percent === 100" class="text-success">100%</span>
                <span v-else>{{ order.sewing_done_percent }}%</span>
              </small>
              <br>
              <small>
                Zafakturowane:
                <span v-if="order.invoiced" class="text-success">Tak</span>
                <span v-else class="text-danger">Nie</span>
              </small>
            </td>
            <td
              class="align-middle"
              style="width: 250px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zamowienia/edycja/' + order.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>

              <d-button
                theme="info"
                class="mr-2 text-nowrap"
                :disabled="downloadInProgress === order.id"
                @click.prevent.stop="downloadPdf(order.id)"
                title="Pobierz oznaczenie">
                <div
                  v-if="downloadInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-if="downloadInProgress !== order.id"
                  class="material-icons">
                  cloud_download
                </i>
              </d-button>

              <d-button
                theme="warning"
                class="mr-2 text-nowrap"
                :disabled="!order.has_label_token || downloadLabelInProgress === order.id"
                @click.prevent.stop="downloadLabel(order.id, order.has_label_token)"
                title="Pobierz etykietę dla dostawcy">
                <div
                  v-if="downloadLabelInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-if="downloadLabelInProgress !== order.id"
                  class="material-icons">
                  cloud_download
                </i>
              </d-button>

              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeOrder(order.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>

              <d-button
                v-if="!order.invoiced"
                theme="success"
                :disabled="invoiceInProgress"
                @click.native="markAsInvoiced(order.id)"
                :outline="true"
                title="Oznacz jako zafakturowane"
                class="mt-2">
                Oznacz jako zafakturowane
              </d-button>

              <d-button
                v-if="order.invoiced"
                theme="danger"
                :disabled="invoiceInProgress"
                @click.native="markAsNotInvoiced(order.id)"
                :outline="true"
                title="Cofnij zafakturowanie"
                class="mt-2">
                Cofnij zafakturowanie
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak zamówień do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="showProductsPopup"
      size="lg"
      @close="showProductsPopup = false">
      <d-modal-header>
        <d-modal-title>
          Produkty w zamówieniu <span class="text-primary">{{ popupOrderName }}</span>
        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="p-0 pb-3">
        <table class="table table-sm w-100">
          <thead>
            <tr>
              <th class="pl-4">Produkt</th>
              <th>Polar</th>
              <th>Rozmiar</th>
              <th>Ilość</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(product, index) of popupProducts"
              :key="'product-from-order-' + index">
              <td class="pl-4">
                {{ product.name }}
              </td>
              <td>
                {{ product.polar_name }}
              </td>
              <td>
                {{ product.size_name }}
              </td>
              <td>
                {{ product.quantity }} szt.
              </td>
            </tr>
          </tbody>
        </table>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Vue from 'vue';

export default {
  name: 'orders',
  props: {
    inPopup: {
      default: false
    },
    workerID: {
      default: false
    }
  },
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'select-customer': SelectCustomer
  },
  filters: {
    dateStart (input) {
      if (input === null) {
        return ' szukaj od ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    dateEnd (input) {
      if (input === null) {
        return ' do ';
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    orderStatusName () {
      return {
        'NEW': 'Nowe',
        'IN_PROGRESS': 'Trwające',
        'SENT_TO_SETTLEMENT': 'Wysłano do rozliczenia',
        'DONE': 'Ukończone',
        'CANCELLED': 'Anulowane'
      };
    },
    orderStatusClass () {
      return {
        'NEW': 'primary',
        'IN_PROGRESS': 'warning',
        'SENT_TO_SETTLEMENT': 'info',
        'DONE': 'success',
        'CANCELLED': 'danger'
      };
    },
    orderStatusOptions () {
      return [{
        text: 'Dowolny',
        value: ''
      },
      {
        text: 'Nowe',
        value: 'NEW'
      },
      {
        text: 'Trwające',
        value: 'IN_PROGRESS'
      },
      {
        text: 'Wysłano do rozliczenia',
        value: 'SENT_TO_SETTLEMENT'
      },
      {
        text: 'Ukończone',
        value: 'DONE'
      },
      {
        text: 'Anulowane',
        value: 'CANCELLED'
      }];
    },
    hasSelectedItems () {
      return Object.values(this.selectedItems).includes(true);
    }
  },
  watch: {
    hasSelectedItems (newValue) {
      if (newValue) {
        this.toggleAllItems = true;
      } else {
        this.toggleAllItems = false;
      }
    }
  },
  data() {
    return {
      orders: [],
      loaded: false,
      loadedWorkers: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        customer_id: null,
        status: null,
        worker_id: null
      },
      dateRange: {
        startDate: null,
        endDate: null
      },
      downloadInProgress: false,
      downloadLabelInProgress: false,
      workers: [],
      popupProducts: [],
      popupOrderName: '',
      showProductsPopup: false,
      invoiceInProgress: false,
      selectedItems: {},
      toggleAllItems: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
    this.$bus.$on('ui-select-customer-updated', this.updateCustomerID);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.worker_id) {
        where.worker_id = parseInt(this.filters.worker_id.value, 10);
      }

      if (this.filters.status && this.filters.status.value) {
        where.status = this.filters.status.value;
      }

      if (this.filters.customer_id) {
        where.customer_id = parseInt(this.filters.customer_id, 10);
      }

      if (this.workerID) {
        where.worker_id = parseInt(this.workerID, 10);
      }

      if (this.dateRange.startDate) {
        where.planned_shipment_from = this.prepareDateString(this.dateRange.startDate);
      }

      if (this.dateRange.endDate) {
        where.planned_shipment_to = this.prepareDateString(this.dateRange.endDate);
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/orders/items?page=' + this.currentPage,
        listField: 'orders',
        pagination: true,
        params: {
          where
        },
        successAction: () => {
          Vue.set(this, 'selectedItems', {});

          for (let i = 0; i < this.orders.length; i++) {
            let id = this.orders[i].id;
            Vue.set(this.selectedItems, id, false);
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: 'WORKER'
          }
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        customer_id: null,
        status: null,
        worker_id: null
      };
      this.dateRange.startDate = null;
      this.dateRange.endDate = null;
      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-filters-form'], this.debouncedLoadFilteredData);
    },
    removeOrder (orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/orders/delete/',
        id: orderID,
        successTitle: 'Usunięto zamówienie',
        successText: 'Wybrane zamówienie zostało usunięte',
        confirmTitle: 'Czy na pewno chcesz usunąć to zamówienie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie zamówienia nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    plannedShipmentDateColor (dateToCheck) {
      let timestamp = +new Date(dateToCheck);
      let currentTimestamp = +new Date();

      if (timestamp - currentTimestamp < 0) {
        return 'text-danger font-weight-bold';
      } else if (timestamp - currentTimestamp < 4 * 24 * 60 * 60 * 1000) {
        return 'text-warning';
      }

      return 'text-primary';
    },
    orderStatus (orderData) {
      if (orderData.products_with_procedure_count < orderData.order_products_count) {
        return 'text-danger';
      } else if (
        orderData.products_with_procedure_count === orderData.order_products_count &&
        orderData.products_done_count === orderData.order_products_count
      ) {
        return 'text-success';
      }

      return 'text-dark';
    },
    showOrderProducts (name, products) {
      this.popupOrderName = name;
      this.popupProducts = JSON.parse(JSON.stringify(products));
      this.showProductsPopup = true;
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    downloadPdf (orderID) {
      this.downloadInProgress = orderID;
      this.$http.post('/orders/box-marking/' + orderID, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = 'oznaczenie-zamowienie-';
        link.setAttribute('download', filenamePrefix + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.downloadInProgress = false;
      });
    },
    downloadLabel (orderID, hasLabelToken) {
      if (!hasLabelToken) {
        return;
      }

      this.downloadLabelInProgress = orderID;
      this.$http.post('/orders/download-label/' + orderID, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = 'etykieta-dla-dostawcy-';
        link.setAttribute('download', filenamePrefix + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadLabelInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.downloadLabelInProgress = false;
      });
    },
    updateCustomerID (newValue) {
      this.filters.customer_id = newValue;
    },
    markAsInvoiced (orderID) {
      Vue.swal({
        title: 'Podaj numer faktury',
        input: 'text',
        inputLabel: 'Numer faktury',
        showCancelButton: true,
        confirmButtonText: 'Oznacz jako zafakturowane',
        cancelButtonText: 'Anuluj',
        preConfirm: (value) => {
          if (!value) {
            Vue.swal.showValidationMessage('Numer faktury jest wymagany');
            return false;
          }

          return value;
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.invoiceInProgress = true;

          FormUtils.submitInBackground(this, {
            endpoint: '/orders/change-invoiced/' + orderID + '?invoiced=1&invoice_number=' + encodeURIComponent(result.value),
            errorTitle: 'Wystąpił błąd',
            errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
            successAction: () => {
              this.invoiceInProgress = false;
              this.debouncedLoadFilteredData();
            },
            failAction: () => {
              this.invoiceInProgress = false;
            }
          });
        }
      });
    },
    markManyAsInvoiced () {
      Vue.swal({
        title: 'Podaj numer faktury',
        input: 'text',
        inputLabel: 'Numer faktury',
        showCancelButton: true,
        confirmButtonText: 'Oznacz jako zafakturowane',
        cancelButtonText: 'Anuluj',
        preConfirm: (value) => {
          if (!value) {
            Vue.swal.showValidationMessage('Numer faktury jest wymagany');
            return false;
          }

          return value;
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.invoiceInProgress = true;
          let orders = Object.entries(this.selectedItems).filter(pair => pair[1] === true).map(([key]) => parseInt(key, 10));

          FormUtils.submitInBackground(this, {
            endpoint: '/orders/change-many-invoiced',
            errorTitle: 'Wystąpił błąd',
            errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
            params: {
              orders,
              invoiced: 1,
              invoice_number: result.value
            },
            successAction: (response) => {
              if (!response.success) {
                Vue.swal({
                  title: 'Zapis nie powiódł się',
                  html: 'Zapisanie zmian nie powiodło się - spróbuj ponownie',
                  icon: 'warning',
                  confirmButtonText: 'OK',
                  buttonsStyling: true
                });
              }

              this.invoiceInProgress = false;
              this.debouncedLoadFilteredData();
            },
            failAction: () => {
              this.invoiceInProgress = false;
            }
          });
        }
      });
    },
    markAsNotInvoiced (orderID) {
      this.invoiceInProgress = true;

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/change-invoiced/' + orderID + '?invoiced=0',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
        successAction: () => {
          this.invoiceInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.invoiceInProgress = false;
        }
      });
    },
    markManyAsNotInvoiced () {
      this.invoiceInProgress = true;
      let orders = Object.entries(this.selectedItems).filter(pair => pair[1] === true).map(([key]) => parseInt(key, 10));

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/change-many-invoiced',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
        params: {
          invoiced: 0,
          orders
        },
        successAction: (response) => {
          if (!response.success) {
            Vue.swal({
              title: 'Zapis nie powiódł się',
              html: 'Zapisanie zmian nie powiodło się - spróbuj ponownie',
              icon: 'warning',
              confirmButtonText: 'OK',
              buttonsStyling: true
            });
          }

          this.invoiceInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.invoiceInProgress = false;
        }
      });
    },
    updateAllItems () {
      Vue.nextTick(() => {
        if (this.toggleAllItems) {
          for (let i = 0; i < this.orders.length; i++) {
            let id = this.orders[i].id;
            Vue.set(this.selectedItems, id, true);
          }
        } else {
          for (let i = 0; i < this.orders.length; i++) {
            let id = this.orders[i].id;
            Vue.set(this.selectedItems, id, false);
          }
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.updateCustomerID);
  }
};
</script>

<style lang="scss">
tr.has-unread-logs td {
  background: rgba(255, 0, 0, .125);
}

.offset-special {
  right: -30px;
  position: relative;
}

.page-header .d-flex {
  input,
  button {
    z-index: 1;
  }
}
</style>
